import React from 'react';
import { Box, BoxProps, IconButton } from '@mui/material';
import { CopyIcon } from '@uplift/icons';
import { useIsMobile } from '@uplift/hooks';
import { ClickToCopy } from '../../../../ClickToCopy';
import { Button } from '../../../../Button';
import { LoadableTypography } from '../../../../LoadableTypography';
import { InfoRow } from './InfoRow';
import { ChevronRight } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';

export type ContactInfoActionButtonProps = {
  type: 'email' | 'phone' | 'link';
  value?: string;
};

export const ContactInfoActionButton: React.VFC<ContactInfoActionButtonProps> = ({ type, value }) => {
  const location = useLocation();

  const hrefByType = React.useMemo(() => {
    if (type === 'email') {
      return `mailto:${value}`;
    }
    if (type === 'phone') {
      return `tel:${value}`;
    }

    return value;
  }, [ type, value ]);

  const buttonTitleByType = React.useMemo(() => {
    if (type === 'email') {
      return 'Email';
    }

    if (type === 'phone') {
      return 'Call';
    }

    return undefined;
  }, [ type ]);

  if (type === 'link') {
    return (
      <IconButton
        size="small"
        component={Link}
        to={{ pathname: value ?? '', state: { referrer: location.pathname } }}
        style={{ paddingRight: '8px' }}
        disableRipple
      >
        <ChevronRight color="secondary" fontSize="small" />
      </IconButton>
    );
  }

  return (
    <Button size="small" color="secondary" href={hrefByType}>
      {buttonTitleByType}
    </Button>
  );
};


export type ContactInfoRowProps = {
  type: 'email' | 'phone';
  label: string;
  loading: boolean;
  loadingWidth: string;
  value?: string;
} & BoxProps;


export const ContactInfoRow: React.FC<ContactInfoRowProps> = ({
  type,
  label,
  value,
  loading,
  loadingWidth,
  ...boxProps
}) => {
  const isMobile = useIsMobile();

  return (
    <Box {...boxProps} display="flex" alignItems="flex-end" justifyContent="space-between" width="100%">
      <InfoRow label={label}>
        <Box display="flex" alignItems="center" gap="12px">
          <LoadableTypography style={{ lineBreak: 'anywhere' }} width={loadingWidth} loading={loading}>
            {value || '–'}
          </LoadableTypography>
          {value && (
            <ClickToCopy text={value || ''}>
              <IconButton size="small">
                <CopyIcon fontSize="small" />
              </IconButton>
            </ClickToCopy>
          )}
        </Box>
      </InfoRow>
      {isMobile && (
        <ContactInfoActionButton type={type} value={value} />
      )}
    </Box>
  );
};
